import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const navLinks = [
  { href: "#inicio",       label: "Inicio" },
  { href: "#terapias",     label: "Terapias" },
  { href: "#evaluaciones", label: "Evaluaciones" },
  { href: "#equipo",       label: "Equipo" },
  { href: "#contacto",     label: "Contacto" },
];

function scrollTo(href) {
  const el = document.querySelector(href);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 76, behavior: "smooth" });
}

function Logo({ className = "" }) {
  return (
    <a
      href="#inicio"
      onClick={(e) => { e.preventDefault(); scrollTo("#inicio"); }}
      className={`flex items-center flex-shrink-0 ${className}`}
      aria-label="CIND – Inicio"
    >
      <img
        src="/logo.png"
        alt="CIND – Centro Integral del Neurodesarrollo"
        className="h-10 w-auto object-contain"
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextSibling.style.display = "flex";
        }}
      />
      <span
        className="hidden items-center gap-1 font-serif font-bold text-xl text-navy"
        aria-hidden="true"
      >
        CIND
      </span>
    </a>
  );
}

export default function Header() {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollTo(href);
  };

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:bg-white focus:text-primary focus:px-4 focus:py-2 focus:rounded-lg focus:font-semibold focus:shadow-lg"
      >
        Ir al contenido principal
      </a>

      <header
        className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow duration-200 ${
          scrolled ? "shadow-nav" : ""
        }`}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-[76px] flex items-center justify-between gap-6">

          <Logo />

          {/* Desktop nav — centrado */}
          <nav
            aria-label="Navegación principal"
            className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2"
          >
            {navLinks.map(({ href, label }) => (
              <a
                key={href}
                href={href}
                onClick={(e) => handleNav(e, href)}
                className="font-sans text-sm font-medium text-txt hover:text-primary transition-colors duration-150 whitespace-nowrap"
              >
                {label}
              </a>
            ))}
          </nav>

          {/* CTA + hamburger */}
          <div className="flex items-center gap-3 ml-auto md:ml-0">
            <a
              href="#contacto"
              onClick={(e) => handleNav(e, "#contacto")}
              className="btn-primary hidden sm:inline-flex text-sm px-5 py-2.5"
              style={{ minHeight: "40px" }}
            >
              Reservar Cita
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden w-11 h-11 flex items-center justify-center rounded-lg text-txt hover:bg-muted transition-colors"
              aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              id="mobile-nav"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="md:hidden bg-white border-t border-border overflow-hidden"
            >
              <nav aria-label="Navegación móvil" className="px-5 py-4 flex flex-col gap-1">
                {navLinks.map(({ href, label }, i) => (
                  <motion.a
                    key={href}
                    href={href}
                    onClick={(e) => handleNav(e, href)}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="font-sans text-base font-medium text-txt hover:text-primary hover:bg-muted px-4 py-3 rounded-lg transition-colors"
                    style={{ minHeight: "44px", display: "flex", alignItems: "center" }}
                  >
                    {label}
                  </motion.a>
                ))}
                <a
                  href="#contacto"
                  onClick={(e) => handleNav(e, "#contacto")}
                  className="btn-primary mt-3 w-full"
                >
                  Reservar Cita
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
